import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, Typography,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Divider, Alert, CircularProgress,
  Stack,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { generateCierreCajaPDF } from '../../services/cierreCajaPDF';
import { CLP, FINANCE_COLORS, fmtFechaHora, labelMedio } from '../../utils/finance';

const { PRIMARY, SUCCESS, AMBER, DANGER } = FINANCE_COLORS;

// Vista de solo lectura del cierre de un turno, con las ventas que lo componen.
export default function DetalleCierreDialog({ open, onClose, turno, branch }) {
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !turno?.id) return;
    let cancelado = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const snap = await getDocs(query(collection(db, 'Sales'), where('turnoId', '==', turno.id)));
        if (cancelado) return;
        setVentas(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)),
        );
      } catch (e) {
        if (!cancelado) setError('Error al cargar las ventas del turno: ' + e.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [open, turno?.id]);

  if (!turno) return null;

  const diferencia = Number(turno.diferencia) || 0;
  const difColor = diferencia === 0 ? SUCCESS : diferencia > 0 ? AMBER : DANGER;
  const difLabel = diferencia === 0
    ? 'Cuadrado'
    : diferencia > 0 ? `Sobrante de ${CLP(diferencia)}` : `Faltante de ${CLP(Math.abs(diferencia))}`;
  const efectivoEsperado = (Number(turno.montoApertura) || 0) + (Number(turno.totalEfectivo) || 0);

  const medios = [
    ['Efectivo', turno.totalEfectivo], ['Débito', turno.totalDebito], ['Crédito', turno.totalCredito],
    ['Transferencia', turno.totalTransferencia], ['Otros', turno.totalOtros],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: PRIMARY }}>
        Detalle de cierre · {turno.cajaNombre} · {turno.fechaTurno}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Vista de solo lectura. Los montos corresponden al cierre registrado del turno.
        </Alert>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Turno</Typography>
            <Info label="Apertura" value={fmtFechaHora(turno.aperturaAt)} />
            <Info label="Cierre" value={fmtFechaHora(turno.cierreAt)} />
            <Info label="Cajero apertura" value={turno.usuarioAperturaNombre || '–'} />
            <Info label="Cajero cierre" value={turno.usuarioCierreNombre || '–'} />
            <Info label="Sucursal" value={turno.branchName || '–'} />
            <Box sx={{ mt: 1 }}>
              <Chip size="small" label={turno.estado} color={turno.estado === 'cuadrado' ? 'success' : 'warning'} />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Ventas por medio de pago</Typography>
            <Table size="small">
              <TableBody>
                {medios.map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell sx={{ border: 0, py: 0.3 }}>{k}</TableCell>
                    <TableCell align="right" sx={{ border: 0, py: 0.3 }}>{CLP(v)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, borderTop: '2px solid #E2E8F0' }}>Total ventas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, borderTop: '2px solid #E2E8F0' }}>
                    {CLP(turno.totalBruto)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Chip label={`${turno.conteoVentas || 0} transacciones`} size="small" sx={{ mt: 1 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Arqueo de efectivo</Typography>
            <Info label="Fondo de apertura" value={CLP(turno.montoApertura || 0)} />
            <Info label="Efectivo esperado" value={CLP(efectivoEsperado)} bold />
            <Info label="Efectivo contado" value={CLP(turno.cuadreEfectivo || 0)} />
            <Chip label={difLabel} sx={{ mt: 1, bgcolor: difColor, color: '#fff', fontWeight: 700 }} />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Totales tributarios</Typography>
            <Info label="Neto" value={CLP(turno.totalNeto || 0)} />
            <Info label="IVA" value={CLP(turno.totalIVA || 0)} />
            <Info label="Descuentos" value={CLP(turno.totalDescuentos || 0)} />

            {turno.observacionesCierre && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700}>Observaciones</Typography>
                <Typography variant="body2" color="text.secondary">{turno.observacionesCierre}</Typography>
              </>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Ventas del turno
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                    <TableCell>Hora</TableCell>
                    <TableCell>Propietario</TableCell>
                    <TableCell>N° Boleta</TableCell>
                    <TableCell>Medio</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ventas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        Sin ventas registradas en este turno.
                      </TableCell>
                    </TableRow>
                  ) : ventas.map((v) => (
                    <TableRow key={v.id} hover sx={{ opacity: v.estado === 'anulada' ? 0.5 : 1 }}>
                      <TableCell>{fmtFechaHora(v.createdAt).split(' ')[1] || '–'}</TableCell>
                      <TableCell>{v.ownerName || v.patientName || '–'}</TableCell>
                      <TableCell>{v.folioDocumento || v.boletaNumber || '–'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {(v.pagos || [{ medio: v.paymentMethod }]).map((p, i) => (
                            <Chip key={i} label={labelMedio(p.medio)} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(v.totalFinal || v.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button startIcon={<PrintIcon />}
          onClick={() => generateCierreCajaPDF(turno, { branch: branch || {}, reimpresion: true })}>
          Reimprimir cierre
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: PRIMARY, fontWeight: 700 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function Info({ label, value, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>{value}</Typography>
    </Box>
  );
}
